import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Field, Stat, StatusBadge, Textarea } from "@/components/UI";
import { Plus, Trash, Coin, Receipt, CheckCircle, Warning, PencilSimple, Clock, UserCircle, Calendar as CalendarIcon, ChartPieSlice, Leaf, CaretRight, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CATS, PARTY_TYPES, PAYMENT_TYPES, LEAVE_REASONS, MONTHS_TR, today, fmt } from "./constants";

export function FinanceRecords() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [form, setForm] = useState({ type: "gider", category: "yem", amount: "", date: today(), description: "" });

  const load = () => {
    api.get("/finance").then(r => setItems(r.data)).catch(() => {});
    api.get("/finance/summary").then(r => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance", { ...form, amount: Number(form.amount) });
      toast.success("Eklendi");
      setForm({ ...form, amount: "", description: "" }); load();
    } catch (err) { toast.error(err?.message || "Hata"); }
  };
  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    try {
      await api.delete(`/finance/${id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(err?.message || "Hata"); load(); }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Stat testId="fin-income" label="Toplam Gelir" value={(summary?.total_income ?? 0).toLocaleString("tr-TR")} unit="₺" tone="ok" />
        <Stat testId="fin-expense" label="Toplam Gider" value={(summary?.total_expense ?? 0).toLocaleString("tr-TR")} unit="₺" tone="warn" />
        <Stat testId="fin-net" label="Net" value={(summary?.net ?? 0).toLocaleString("tr-TR")} unit="₺" tone="accent" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card>
          <h3 className="heading text-lg mb-3">Kayıt Ekle</h3>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Tip *">
              <Select data-testid="fin-type" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option>gelir</option><option>gider</option>
              </Select>
            </Field>
            <Field label="Kategori *">
              <Select data-testid="fin-cat" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {CATS.map(c => <option key={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Tutar (₺) *"><Input type="number" step="0.01" data-testid="fin-amount" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Tarih *"><Input type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Açıklama"><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
            <Button data-testid="fin-submit" type="submit"><Plus size={14} className="inline mr-1" /> Kaydet</Button>
          </form>
        </Card>

        <Card className="lg:col-span-2 !p-0 overflow-hidden">
          <table className="data-table">
            <thead><tr><th>Tarih</th><th>Tip</th><th>Kategori</th><th>Açıklama</th><th className="text-right">Tutar</th><th></th></tr></thead>
            <tbody>
              {items.map(x => {
                const li = x.line_items || [];
                const hasDetail = li.length > 0;
                const isOpen = expanded.has(x.id);
                return (
                  <React.Fragment key={x.id}>
                    <tr data-testid={`fin-row-${x.id}`}>
                      <td className="tabnum">{x.date}</td>
                      <td><StatusBadge status={x.type} /></td>
                      <td>
                        {x.category}
                        {x.is_own_produced && (
                          <span data-testid={`fin-own-badge-${x.id}`}
                                className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-[#E4EEE6] text-[#2D4A36] border border-[#c2d6c8] px-1.5 py-0.5 rounded">
                            <Leaf size={10} weight="fill" /> Kendi Üretimimiz
                          </span>
                        )}
                      </td>
                      <td>
                        {hasDetail ? (
                          <button data-testid={`fin-detail-toggle-${x.id}`} onClick={() => toggle(x.id)}
                                  className="inline-flex items-center gap-1 text-left text-[#2D4A36] hover:underline">
                            {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                            <span>{x.description || "—"}</span>
                            <span className="ml-1 text-[11px] text-[#8a938c] whitespace-nowrap">({li.length} kalem)</span>
                          </button>
                        ) : (x.description || "—")}
                      </td>
                      <td className={`text-right tabnum font-semibold ${x.type === "gelir" ? "text-[#2D4A36]" : "text-[#C45B45]"}`}>{x.type === "gelir" ? "+" : "−"} {Number(x.amount).toLocaleString("tr-TR")} ₺</td>
                      <td><button onClick={() => del(x.id)} className="p-1.5 hover:bg-[#fbeee9] text-[#C45B45] rounded"><Trash size={16} /></button></td>
                    </tr>
                    {hasDetail && isOpen && (
                      <tr data-testid={`fin-detail-${x.id}`} className="bg-[#F8F6F1]">
                        <td colSpan={6} className="!py-2">
                          <div className="pl-6 pr-2">
                            <table className="w-full text-[13px]">
                              <thead>
                                <tr className="text-[#6b746d] text-left">
                                  <th className="py-1 font-medium">Yem Kalemi</th>
                                  <th className="py-1 font-medium text-right">Miktar</th>
                                  <th className="py-1 font-medium text-right">Birim Fiyat</th>
                                  <th className="py-1 font-medium text-right">Tutar</th>
                                </tr>
                              </thead>
                              <tbody>
                                {li.map((it, i) => (
                                  <tr key={`${it.name}-${i}`} className="border-t border-[#ececec]">
                                    <td className="py-1">
                                      {it.name}
                                      {it.is_own_produced && (
                                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold bg-[#E4EEE6] text-[#2D4A36] border border-[#c2d6c8] px-1 py-0.5 rounded">
                                          <Leaf size={9} weight="fill" /> Kendi
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-1 text-right tabnum">{Number(it.kg || 0).toLocaleString("tr-TR")} kg</td>
                                    <td className="py-1 text-right tabnum">{Number(it.unit_price || 0).toLocaleString("tr-TR")} ₺/kg</td>
                                    <td className="py-1 text-right tabnum font-semibold">{Number(it.cost || 0).toLocaleString("tr-TR")} ₺</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="border-t-2 border-[#d9d4c9] font-semibold">
                                  <td className="py-1" colSpan={3}>
                                    Toplam
                                    {x.own_cost > 0 && (
                                      <span className="ml-1 text-[11px] text-[#6b746d] font-normal">
                                        (Kendi: {Number(x.own_cost).toLocaleString("tr-TR")} ₺ · Satın alınan: {Number(x.purchased_cost || 0).toLocaleString("tr-TR")} ₺)
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-1 text-right tabnum">{Number(x.amount).toLocaleString("tr-TR")} ₺</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!items.length && <tr><td colSpan={6} className="text-center py-8 text-[#8a938c]">Kayıt yok</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}

// -------- Payables Tab --------
